import React from 'react';
import { motion } from 'framer-motion';
import AffiliateForm from '../components/AffiliateForm';

const standardPerks = [
  'Start at 5% commission — earn up to 10% as your sales grow',
  'Your audience gets 10% off every ticket purchase',
  'Co-marketing support — festival assets, clips, and promo materials',
  'Personal referral link and dashboard access',
  'Real-time tracking of clicks, conversions, and commissions',
  'Monthly payouts via Stripe Connect (bank transfer or debit card)',
  'Open to everyone — bloggers, podcasters, film students, social media creators',
];

const partnerPerks = [
  'Everything in Standard, plus:',
  'Start at 7.5% commission — earn up to 15% as your sales grow',
  'Priority co-marketing support — custom clips and dedicated promo materials',
  'Featured on the Synema Film Fest affiliates page as an official festival partner',
  'Early access to lineup announcements and exclusive content to share',
  'Dedicated contact at Synema Film Fest for campaign support',
  'Invitation to press screenings and virtual filmmaker events',
  'By application only — reserved for creators with established, engaged audiences',
];

const steps = [
  {
    num: '01',
    title: 'Apply',
    desc: 'Fill out a short form with your platform and audience info.',
  },
  {
    num: '02',
    title: 'Get your link',
    desc: 'Receive a unique referral URL and your asset kit.',
  },
  {
    num: '03',
    title: 'Earn',
    desc: 'Track clicks, conversions, and commissions in your dashboard.',
  },
];

const Affiliates = () => {
  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4">
            Affiliate Program
          </div>
          <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] tracking-[0.04em] leading-none text-white mb-6">
            Earn by spreading the word.
          </h1>
          <p className="text-[1rem] font-light leading-[1.8] text-white/[0.65] max-w-[640px]">
            Share Synema Film Fest with your audience — filmmakers, cinephiles, or creatives — and earn up to 15% commission on every ticket sold through your referral link. The more you sell, the more you earn.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-dark2 border border-teal/20 rounded p-8 relative overflow-hidden flex flex-col"
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-teal to-transparent" />
            <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-teal mb-3">Standard Affiliate</div>
            <h3 className="font-display text-2xl tracking-[0.05em] text-white mb-1">EARN UP TO 10%</h3>
            <p className="text-[0.8rem] text-white/40 mb-6">Open to everyone — commission grows as you sell more</p>
            <ul className="space-y-3 mb-8 flex-1">
              {standardPerks.map((perk, i) => (
                <li key={i} className="flex gap-3 items-start text-[0.88rem] font-light text-white/60 leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0 mt-2" />
                  {perk}
                </li>
              ))}
            </ul>
            <a
              href="#apply"
              className="inline-block w-full bg-teal text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-6 rounded-sm hover:opacity-90 transition-all hover:-translate-y-[1px] text-center mt-auto"
            >
              Sign Up Now
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-dark2 border border-orange/20 rounded p-8 relative overflow-hidden flex flex-col"
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-orange to-transparent" />
            <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-orange mb-3">Partner Affiliate</div>
            <h3 className="font-display text-2xl tracking-[0.05em] text-white mb-1">EARN UP TO 15%</h3>
            <p className="text-[0.8rem] text-white/40 mb-6">By invitation — higher rates for creators with established audiences</p>
            <ul className="space-y-3 mb-8 flex-1">
              {partnerPerks.map((perk, i) => (
                <li key={i} className="flex gap-3 items-start text-[0.88rem] font-light text-white/60 leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0 mt-2" />
                  {perk}
                </li>
              ))}
            </ul>
            <a
              href="#apply"
              className="inline-block w-full btn-gradient text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-6 rounded-sm hover:opacity-90 transition-all hover:-translate-y-[1px] text-center mt-auto"
            >
              Apply for Partner
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4">
            How It Works
          </div>
          <h2 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white mb-8">
            Three steps to earning.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="bg-dark2 border border-white/[0.06] rounded p-6 relative overflow-hidden">
                <div className="font-display text-[4rem] leading-none text-white/[0.04] absolute top-2 right-4">
                  {step.num}
                </div>
                <div className="font-display text-xl text-teal mb-2">{step.title.toUpperCase()}</div>
                <p className="text-[0.88rem] font-light text-white/60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          id="apply"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4 text-center">
            Apply Now
          </div>
          <h2 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white mb-8 text-center">
            Join the program.
          </h2>
          <AffiliateForm />
        </motion.div>
      </div>
    </div>
  );
};

export default Affiliates;
